import { LegalLayout, Section, Bullets } from "../components/LegalLayout";

const Terms = () => {
  return (
    <LegalLayout
      testid="terms-page"
      title="Terms of Service"
      subtitle="The terms that govern your use of CPC Pay, operated by Comprehensive Pain Consultants."
      effectiveDate="June 1, 2026"
    >
      <Section heading="1. Acceptance of Terms">
        <p>
          These Terms of Service ("Terms") are a binding agreement between you and Comprehensive Pain Consultants
          Architect LLC ("CPC," "we," "us," or "our") governing your access to and use of CPC Pay
          (the "Platform"). By creating an account or using the Platform, you agree to these Terms. If
          you do not agree, do not use the Platform.
        </p>
      </Section>

      <Section heading="2. Eligibility & Accounts">
        <Bullets
          items={[
            "You must be at least 18 years old and able to form a binding contract.",
            "You agree to provide accurate, current, and complete information and to keep it up to date.",
            "You are responsible for all activity under your account and for maintaining the confidentiality of your credentials and multi-factor authentication factors.",
            "Multi-factor authentication is mandatory; you must keep your authenticator and backup codes secure.",
          ]}
        />
      </Section>

      <Section heading="3. The Service">
        <p>
          CPC Pay lets vendors and employees link a bank account through Plaid and send or receive
          payments across supported rails (ACH, Same-Day ACH, RTP, and Wire). Availability,
          processing times, and limits depend on the selected rail, your financial institution, and
          applicable network rules. We may add, modify, or discontinue features at any time.
        </p>
      </Section>

      <Section heading="4. Payments, Authorization & NACHA">
        <Bullets
          items={[
            "By initiating or authorizing a transfer, you represent that you own or are authorized to use the connected account and that the transaction is lawful.",
            "You authorize CPC and its payment partners to debit or credit your account as needed to complete the transactions you request, in accordance with NACHA Operating Rules and applicable network rules.",
            "You are responsible for ensuring sufficient funds and for any fees, returns, or reversals associated with your transactions.",
            "Transfers may be delayed, held, or declined for fraud prevention, compliance, or risk-management reasons.",
          ]}
        />
      </Section>

      <Section heading="5. Bank Connections via Plaid">
        <p>
          Bank account linking is provided by Plaid Inc. Your use of Plaid is governed by Plaid's
          policies and our{" "}
          <a href="/plaid-privacy" className="text-emerald-700 underline hover:text-emerald-800">
            Plaid Privacy disclosure
          </a>
          . You authorize CPC to access account information through Plaid as needed to provide the
          Platform.
        </p>
      </Section>

      <Section heading="6. Acceptable Use">
        <p>You agree not to:</p>
        <Bullets
          items={[
            "Use the Platform for any unlawful, fraudulent, or unauthorized purpose, including money laundering or sanctions evasion.",
            "Attempt to gain unauthorized access to the Platform, other accounts, or our systems, or to bypass security or authentication controls.",
            "Interfere with or disrupt the integrity or performance of the Platform.",
            "Misrepresent your identity or provide false payment or account information.",
          ]}
        />
      </Section>

      <Section heading="7. Privacy">
        <p>
          Your use of the Platform is also governed by our{" "}
          <a href="/privacy-policy" className="text-emerald-700 underline hover:text-emerald-800">
            Privacy Policy
          </a>
          , which explains how we collect, use, and protect your information.
        </p>
      </Section>

      <Section heading="8. Disclaimers">
        <p>
          The Platform is provided "as is" and "as available" without warranties of any kind, whether
          express or implied, including merchantability, fitness for a particular purpose, and
          non-infringement. We do not warrant that the Platform will be uninterrupted, error-free, or
          secure, or that transfers will always be timely.
        </p>
      </Section>

      <Section heading="9. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, CPC and its affiliates will not be liable for any
          indirect, incidental, special, consequential, or punitive damages, or any loss of profits,
          data, or goodwill, arising from or related to your use of the Platform. Nothing in these
          Terms limits liability that cannot be limited under applicable law.
        </p>
      </Section>

      <Section heading="10. Termination">
        <p>
          You may stop using the Platform at any time. We may suspend or terminate your access if you
          violate these Terms, if required for security or legal/compliance reasons, or if we
          discontinue the Platform. Certain obligations and records survive termination as required by
          law.
        </p>
      </Section>

      <Section heading="11. Changes to These Terms">
        <p>
          We may update these Terms from time to time. Material changes will be posted on this page
          with a revised effective date and, where appropriate, communicated within the Platform.
          Continued use after changes take effect constitutes acceptance.
        </p>
      </Section>

      <Section heading="12. Contact Us">
        <p>
          Questions about these Terms? Contact Comprehensive Pain Consultants at{" "}
          <a href="mailto:legal@cpcdoctors.com" className="text-emerald-700 underline hover:text-emerald-800" data-testid="terms-contact-email">
            legal@cpcdoctors.com
          </a>{" "}
          or visit{" "}
          <a href="https://www.cpcdoctors.com/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline hover:text-emerald-800">
            cpcdoctors.com
          </a>
          .
        </p>
      </Section>
    </LegalLayout>
  );
};

export default Terms;
